import { useState, useRef, useCallback, useEffect } from 'react';
import io from 'socket.io-client';
import { WebSocketMessage, Prompt } from '../types';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const connect = useCallback((realtimePromptObj: Prompt | null, tutorPromptObj: Prompt | null) => {
    if (socketRef.current?.connected) {
      console.log('Socket already connected');
      return Promise.resolve();
    }

    setIsConnecting(true);
    console.log('🔗 Attempting to connect to server...');
    
    return new Promise<void>((resolve, reject) => {
      try {
        // Create socket connection
        socketRef.current = io(SERVER_URL, {
          transports: ['websocket', 'polling'],
          timeout: 20000,
        });
        
        const socket = socketRef.current;

        socket.on('connect', () => {
          console.log('✅ Socket.IO connected successfully');
          setIsConnected(true);
          setIsConnecting(false);
          
          // Initialize session after connection
          console.log('🎯 Initializing session...');
          console.log('🎯 Prompt objects:', {
            realtimePrompt: realtimePromptObj,
            tutorPrompt: tutorPromptObj,
            realtimePromptLength: realtimePromptObj?.prompt?.length,
            tutorPromptLength: tutorPromptObj?.prompt?.length,
            realtimePromptVersion: realtimePromptObj?.version,
            tutorPromptVersion: tutorPromptObj?.version
          });
          socket.emit('initializeSession', { 
            clientInfo: 'Fermi Tutor Frontend',
            timestamp: new Date().toISOString(),
            realtimePrompt: realtimePromptObj?.prompt,
            tutorPrompt: tutorPromptObj?.prompt,
            realtimePromptVersion: realtimePromptObj?.version,
            tutorPromptVersion: tutorPromptObj?.version,
            realtimePromptFilePath: realtimePromptObj?.filePath ?? null,
            tutorPromptFilePath: tutorPromptObj?.filePath ?? null
          });
          
          resolve();
        });

        socket.on('sessionStarted', (data: any) => {
          console.log('✅ Session initialized:', data);
        });

        socket.on('connectionStatus', (data: any) => {
          console.log('📡 Connection status:', data);
          if (data.connected) {
            console.log('🔗 Agent connection established');
          }
        });

        socket.on('conversationUpdate', (data: any) => {
          console.log('📝 Conversation update received:', data);
          if (data.type === 'transcript') {
            setLastMessage({ 
              type: 'transcript',
              transcriptType: data.transcriptType, // 'user', 'assistant', or 'tutor'
              transcript: data.transcript,
              isPartial: data.isPartial || false,
              audio_content: data.audioContent ?? null,
              questionUrl: data.questionUrl ?? null,
              solutionUrl: data.solutionUrl ?? null
            });
          }
        });

        socket.on('textContent', (data: any) => {
          console.log('📄 Text content received:', data);
          setLastMessage({
            type: 'tutor_response',
            text_content: data.content,
            audio_content: data.audioContent ?? null
          });
        });

        socket.on('audio', (audioData: any) => {
          console.log('🔊 Audio chunk received:', { 
            format: audioData.format, 
            sampleRate: audioData.sampleRate,
            dataLength: audioData.audio?.byteLength 
          });
          setLastMessage({
            type: 'audio_chunk',
            audioData: audioData
          });
        });

        socket.on('tool_call_start', (data: any) => {
          console.log('🛠️ Tool call started:', data);
          setLastMessage({
            type: 'tool_call_start',
            tool: data.tool
          });
        });

        socket.on('tool_call_end', (data: any) => {
          console.log('✅ Tool call ended:', data);
          setLastMessage({
            type: 'tool_call_end'
          });
        });

        socket.on('imageUpdated', (data: any) => {
          console.log('🖼️ Image updated:', data);
        });

        socket.on('cost_update', (data: any) => {
            console.log('💰 Cost update received:', data);
            setLastMessage({
                type: 'cost_update',
                costs: data
            });
        });

        socket.on('user_transcript_final', (data: any) => {
          console.log('📝 Final user transcript received:', data);
          setLastMessage({
            type: 'user_transcript_final',
            transcript: data.transcript
          });
        });

        socket.on('intent_classified', (data: any) => {
          console.log('🧠 Intent classified:', data);
          setLastMessage({
            type: 'intent_classified',
            intent: data.intent
          });
        });

        socket.on('error', (error: any) => {
          console.error('❌ Socket error:', error);
          setLastMessage({
            type: 'error',
            message: error.message || 'Socket error occurred'
          });
        });

        socket.on('disconnect', (reason: any) => {
          console.log('🔌 Socket disconnected:', reason);
          setIsConnected(false);
          setIsConnecting(false);
        });

        socket.on('connect_error', (error: any) => {
          console.error('❌ Connection error:', error);
          setIsConnecting(false);
          reject(error);
        });
        
      } catch (error) {
        console.error('❌ Socket creation error:', error);
        setIsConnecting(false);
        reject(error);
      }
    });
  }, []);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('🔌 Disconnecting socket...');
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const sendText = useCallback((text: string) => {
    if (socketRef.current?.connected) {
      console.log('📤 Sending text:', text);
      socketRef.current.emit('text', { message: text });
    } else {
      console.error('❌ Socket is not connected');
    }
  }, []);

  const sendTutorRequest = useCallback((
    message: string,
    questionImageUrl?: string,
    solutionImageUrl?: string
  ) => {
    if (!socketRef.current?.connected) {
      console.error('❌ Socket is not connected');
      return;
    }

    console.log('🎓 Sending tutor request:', { message, questionImageUrl, solutionImageUrl });
    
    // Update solution image if provided
    if (solutionImageUrl) {
      socketRef.current.emit('updateSolutionImage', { url: solutionImageUrl });
    }

    if (questionImageUrl) {
      socketRef.current.emit('updateQuestionImage', { url: questionImageUrl });
    }
    
    // Send the text message
    socketRef.current.emit('text', { message });
  }, []);

  const sendAudio = useCallback((
    audioData: Int16Array,
    questionImageUrl?: string,
    solutionImageUrl?: string
  ) => {
    if (!socketRef.current?.connected) {
      console.error('❌ Socket is not connected');
      return;
    }

    console.log('🎤 Sending audio with tutor request:', { questionImageUrl, solutionImageUrl });

    if (solutionImageUrl) {
      socketRef.current.emit('updateSolutionImage', { url: solutionImageUrl });
    }

    if (questionImageUrl) {
      socketRef.current.emit('updateQuestionImage', { url: questionImageUrl });
    }

    // Send as a binary ArrayBuffer
    socketRef.current.emit('audio', audioData.buffer);
  }, []);

  const stopAudioStream = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log('📤 Sending end of audio stream signal');
      // Sending null signals the backend to finalize the transcription
      socketRef.current.emit('audio', null);
    } else {
      console.error('❌ Socket is not connected for stopping audio stream');
    }
  }, []);

  const sendScreenData = useCallback((screenData: any) => {
    if (socketRef.current?.connected) {
      console.log('📺 Sending screen data...');
      socketRef.current.emit('screenData', screenData);
    } else {
      console.error('❌ Socket is not connected');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  return {
    isConnected,
    isConnecting,
    lastMessage,
    connect,
    disconnect,
    sendText,
    sendTutorRequest,
    sendAudio,
    stopAudioStream,
    sendScreenData
  };
};
